import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, UsersThree, Handshake, Buildings, Compass } from "@phosphor-icons/react";
import { Reveal } from "../components/Reveal";
import { Marquee } from "../components/Marquee";
import { IMAGES, STATS, COMPANY } from "../data/site";

const VALUES = [
  { icon: UsersThree, title: "Familie zuerst", text: "Inhaber R. Sylka steht persönlich hinter jedem Projekt — kurze Wege, klare Worte, ein Ansprechpartner." },
  { icon: Handshake, title: "Handschlagqualität", text: "Was wir versprechen, halten wir. Termine, Budgets, Qualität — seit 30 Jahren ohne Wenn und Aber." },
  { icon: Buildings, title: "Regionale Wurzeln", text: "Offenbach am Main ist unser Zuhause. Den Raum Frankfurt kennen wir wie unsere Westentasche." },
  { icon: Compass, title: "Alles aus einer Hand", text: "Rohbau, Umbau, Sanierung und Gartenbau — ein Team, ein Verantwortlicher, ein Ergebnis." },
];

export default function UeberUns() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-12%", "12%"]);

  return (
    <main data-testid="ueber-uns-page">
      <section className="border-b border-line">
        <div className="mx-auto max-w-[1600px] px-6 pb-20 pt-40 md:px-12 md:pt-52">
          <Reveal>
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-ember">Über uns</p>
            <h1 className="mt-8 font-display text-5xl font-black uppercase leading-[0.95] tracking-tighter text-bone md:text-8xl">
              Bauen ist<br />Vertrauenssache
            </h1>
          </Reveal>
          <div className="mt-16 grid gap-12 lg:grid-cols-2">
            <Reveal delay={0.1}>
              <p className="text-lg leading-relaxed text-bone md:text-xl">
                Die Prott Bauunternehmung ist ein familiengeführtes Unternehmen aus Offenbach am Main — mit 30-jähriger Expertise in Rohbau, Umbau, Sanierung/Renovierung und Garten- & Landschaftsbau.
              </p>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="text-base leading-relaxed text-ash">
                Was als Familienbetrieb begann, ist heute ein gefragter Partner für öffentliche und private Bauherren in der gesamten Rhein-Main-Region und darüber hinaus. Ob Kulturdenkmal wie die Alte Oper Frankfurt, Forschungsbau für die GSI Darmstadt oder ganze Wohnquartiere in Langen, Weinheim und Würzburg — wir bauen so, als wäre es unser eigenes Zuhause. Weil hinter jedem Auftrag ein Name steht: unsere Familie.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      <section ref={ref} className="relative overflow-hidden border-b border-line">
        <motion.img
          style={{ y }}
          src={IMAGES.frankfurt}
          alt="Frankfurter Skyline bei Nacht — die Heimatregion der Prott Bauunternehmung"
          className="h-[50vh] w-full scale-125 object-cover md:h-[70vh]"
        />
        <div className="absolute inset-0 bg-black/50" aria-hidden="true" />
        <div className="absolute inset-x-0 bottom-0 border-t border-bone/10 bg-ink/60 backdrop-blur-md">
          <div className="mx-auto grid max-w-[1600px] grid-cols-2 lg:grid-cols-4">
            {STATS.map((s, i) => (
              <div key={s.label} className={`px-6 py-8 md:px-12 ${i > 0 ? "border-l border-bone/10" : ""}`} data-testid={`about-stat-${i}`}>
                <p className="font-display text-3xl font-bold text-bone md:text-4xl">{s.value}<span className="text-ember">{s.suffix}</span></p>
                <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.2em] text-ash">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-[1600px] px-6 py-24 md:px-12 md:py-32">
        <Reveal>
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-ember">Unsere Haltung</p>
          <h2 className="mt-6 font-display text-4xl font-bold uppercase tracking-tighter text-bone md:text-6xl">
            Woran wir uns messen lassen
          </h2>
        </Reveal>
        <div className="mt-16 grid gap-px border border-line bg-line md:grid-cols-2">
          {VALUES.map((v, i) => (
            <Reveal key={v.title} delay={i * 0.08}>
              <div className="group h-full bg-ink p-10 transition-colors duration-500 hover:bg-coal md:p-14" data-testid={`value-card-${i}`}>
                <v.icon size={32} className="text-ember" />
                <p className="mt-10 font-mono text-xs text-ember">{String(i + 1).padStart(2, "0")}</p>
                <h3 className="mt-4 font-display text-2xl font-semibold uppercase tracking-tight text-bone">{v.title}</h3>
                <p className="mt-4 text-sm leading-relaxed text-ash">{v.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="border-t border-line bg-coal">
        <div className="mx-auto flex max-w-[1600px] flex-col items-start gap-10 px-6 py-24 md:flex-row md:items-center md:justify-between md:px-12 md:py-32">
          <Reveal>
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-ember">Ihr Ansprechpartner</p>
            <h2 className="mt-6 font-display text-4xl font-bold uppercase tracking-tighter text-bone md:text-5xl">
              {COMPANY.owner}
            </h2>
            <p className="mt-4 max-w-md text-base leading-relaxed text-ash">
              Inhaber und Ihr direkter Draht zu jedem Bauvorhaben. Rufen Sie an — wir sprechen Klartext.
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="flex flex-col gap-4">
              <a
                href={COMPANY.phoneHref}
                data-testid="about-phone-button"
                className="group inline-flex items-center gap-3 bg-ember px-8 py-4 font-mono text-xs uppercase tracking-[0.2em] text-ink transition-colors duration-300 hover:bg-bone"
              >
                {COMPANY.phone}
                <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
              </a>
              <Link
                to="/kontakt"
                data-testid="about-contact-button"
                className="group inline-flex items-center justify-center gap-3 border border-bone/30 px-8 py-4 font-mono text-xs uppercase tracking-[0.2em] text-bone transition-colors duration-300 hover:border-ember hover:text-ember"
              >
                Nachricht schreiben
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      <Marquee />
    </main>
  );
}
